// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';

// material ui
import { Button, Grid, Popover, Typography } from '@mui/material';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { daDK } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';

// components
import { useUrlComparisonDateRange } from '@/hooks/useUrlComparisonDateRange';
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

import { DateTime } from 'luxon';

// ---------- Helpers ----------
const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  const year = date.setLocale('da').toFormat('yyyy');
  return `${day} ${month} ${year}`;
};

// Snap BACK to a specific weekday (1..7, Mon..Sun)
const alignToWeekday = (date, anchorWeekday) => {
  const diff = (date.weekday - anchorWeekday + 7) % 7;
  return date.minus({ days: diff });
};

// Inclusive end-of-range from a start
const computeInclusiveEnd = (start, aggregate, globalDuration) => {
  if (aggregate === 'month') return start.endOf('month');
  const days = Math.max(0, Math.round(globalDuration) - 1);
  return start.plus({ days }).endOf('day');
};

// Previous (shifted) comparison start, weekday-aligned
const getComparisonStart = (date, aggregate, globalDuration, anchorWeekday) => {
  if (aggregate === 'month') return date.startOf('month').minus({ months: 1 });
  const days = Math.max(0, Math.round(globalDuration));
  const target = date.minus({ days });
  return alignToWeekday(target, anchorWeekday);
};

const SingleDateSelector = ({
  isComparingSameNode,
  aggregate, // 'month' or day-based
}) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const [anchorEl, setAnchorEl] = useState(null);

  const { timeRange, durationDays } = useUrlDateRange();
  const { comparisonTimeRange, setComparisonRange } =
    useUrlComparisonDateRange();

  // Base start-of-day from current timeRange (local zone)
  const baseStart = useMemo(
    () =>
      DateTime.fromMillis(timeRange.start)
        .setZone('Europe/Copenhagen')
        .startOf('day'),
    [timeRange.start],
  );

  // Anchor weekday is taken from the ORIGINAL timeRange.start
  const anchorWeekday = useMemo(
    () =>
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen').weekday,
    [timeRange.start],
  );

  const [selectedDate, setSelectedDate] = useState(baseStart);
  const [selectedDateEnd, setSelectedDateEnd] = useState(
    computeInclusiveEnd(baseStart, aggregate, durationDays),
  );

  // Track whether the user has taken an action; prevents future auto-defaults
  const userTouchedRef = useRef(false);

  // Disable all dates that are not the anchored weekday
  const allowedWeekday = anchorWeekday; // 1..7 (Mon..Sun)
  const shouldDisableDate = (date) => date.weekday !== allowedWeekday;

  const commitRange = useCallback(
    (start, end) => {
      setSelectedDate(start);
      setSelectedDateEnd(end);

      const newStart = start.toUTC().toMillis();
      const newEnd = end.toUTC().toMillis();

      if (
        !comparisonTimeRange ||
        comparisonTimeRange.start !== newStart ||
        comparisonTimeRange.end !== newEnd
      ) {
        setComparisonRange(start, end, {
          roundToDay: false,
          clampToNow: true,
        });
      }
    },
    [setComparisonRange, comparisonTimeRange],
  );

  const handleDateChange = (date) => {
    if (!date) return;
    userTouchedRef.current = true;

    const calculatedStart = date.startOf('day'); // picker enforces weekday
    const calculatedEnd = computeInclusiveEnd(
      calculatedStart,
      aggregate,
      durationDays,
    );

    commitRange(calculatedStart, calculatedEnd);
    setAnchorEl(null);
  };

  /**
   * Sync to external timeRange changes (or switching same-node compare on/off).
   * IMPORTANT: We intentionally do NOT depend on `aggregate` or `globalDuration`
   * here to avoid resetting the comparison range when `viewMode` changes.
   */
  useEffect(() => {
    // If user has interacted, don't auto-reset.
    if (userTouchedRef.current) return;

    const start = baseStart;

    let calculatedStart;
    let calculatedEnd;

    if (isComparingSameNode) {
      calculatedStart = getComparisonStart(
        start,
        aggregate, // ok to read current aggregate; effect doesn't re-run on it
        durationDays,
        anchorWeekday,
      );
      calculatedEnd = computeInclusiveEnd(
        calculatedStart,
        aggregate,
        durationDays,
      );
    } else {
      calculatedStart = start;
      calculatedEnd = DateTime.fromMillis(timeRange.end)
        .setZone('Europe/Copenhagen')
        .endOf('day'); // inclusive end when not shifting
    }

    // Initialize or re-sync if the range meaningfully changed due to timeRange flip
    commitRange(calculatedStart, calculatedEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseStart, timeRange.end, isComparingSameNode, anchorWeekday]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale="da"
      localeText={
        daDK.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <>
        <Grid container alignItems="center" spacing={2}>
          <Grid size="grow">
            <Button
              startIcon={<CalendarTodayRoundedIcon />}
              size="large"
              variant="text"
              disabled={!isComparingSameNode}
              fullWidth
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                textTransform: 'none',
                color: 'text.primary',
              }}
            >
              {selectedDate
                ? `${formatDate(selectedDate)} - ${formatDate(selectedDateEnd)}`
                : t('comparison_analysis.select_start_date')}
            </Button>
          </Grid>
        </Grid>

        {!isComparingSameNode && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            {t('comparison_analysis.date_range_disabled_hint')}
          </Typography>
        )}

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <StaticDatePicker
            displayStaticWrapperAs="mobile"
            value={selectedDate}
            onChange={handleDateChange}
            maxDate={DateTime.now()}
            shouldDisableDate={shouldDisableDate}
          />
        </Popover>
      </>
    </LocalizationProvider>
  );
};

export default SingleDateSelector;
